/**
 * OneSignal Capacitor (Android/iOS) implementation.
 */

import OneSignal, {
  LogLevel,
  type PushSubscriptionChangedState,
} from '@onesignal/capacitor-plugin';
import { Capacitor } from '@capacitor/core';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import {
  clearOneSignalSessionState,
  getOneSignalAppId,
  parseSyncParams,
  runSerializedSync,
  setLastSyncedEmail,
  setLastSyncedPhone,
  setSyncedExternalId,
  type SyncOneSignalUserParams,
} from '@/services/oneSignal/shared';
import {
  deactivatePushSubscriptionOnBackend,
  registerPushSubscriptionWithBackend,
} from '@/services/oneSignal/backendRegistration';

/** Tracks whether OneSignal.initialize has completed successfully. */
let initialized = false;

/** Shared in-flight init promise — prevents duplicate init under React StrictMode. */
let initPromise: Promise<void> | null = null;

/** Ensures the push subscription change listener is registered only once. */
let subscriptionListenerAttached = false;

export function isOneSignalNativeAvailable(): boolean {
  return Capacitor.isNativePlatform() && Boolean(getOneSignalAppId());
}

async function logSubscriptionState(context = 'current'): Promise<void> {
  try {
    const [optedIn, subscriptionId, token] = await Promise.all([
      OneSignal.User.pushSubscription.getOptedInAsync(),
      OneSignal.User.pushSubscription.getIdAsync(),
      OneSignal.User.pushSubscription.getTokenAsync(),
    ]);

    console.log('[OneSignal] User subscription state:', {
      platform: 'native',
      context,
      optedIn,
      subscriptionId,
      token: token ? `${token.slice(0, 8)}…` : null,
    });
  } catch (error) {
    console.error('[OneSignal] Failed to read subscription state:', error);
  }
}

function attachSubscriptionChangeListener(): void {
  if (subscriptionListenerAttached) return;

  OneSignal.User.pushSubscription.addEventListener(
    'change',
    (event: PushSubscriptionChangedState) => {
      console.log('[OneSignal] User subscription state changed:', {
        platform: 'native',
        previous: event.previous,
        current: event.current,
      });

      const nextId = event.current?.id ?? null;
      if (nextId) {
        const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
        void registerPushSubscriptionWithBackend(nextId, platform);
      }
    },
  );

  subscriptionListenerAttached = true;
}

export async function initializeOneSignalNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const appId = getOneSignalAppId();
  if (!appId) {
    console.error(
      '[OneSignal] VITE_ONESIGNAL_APP_ID is not configured. Push notifications are disabled.',
    );
    return;
  }

  if (initialized) return;

  if (initPromise) {
    await initPromise;
    return;
  }

  initPromise = (async () => {
    try {
      if (import.meta.env.DEV) {
        OneSignal.Debug.setLogLevel(LogLevel.Verbose);
      }

      await OneSignal.initialize(appId);

      initialized = true;
      console.log('[OneSignal] initialized');

      attachSubscriptionChangeListener();
      await logSubscriptionState('after-init');
    } catch (error) {
      initPromise = null;
      initialized = false;
      throw error;
    }
  })();

  await initPromise;
}

export async function requestNotificationPermissionNative(): Promise<boolean> {
  await initializeOneSignalNative();

  if (!Capacitor.isNativePlatform() || !getOneSignalAppId()) {
    return false;
  }

  try {
    const alreadyPrompted = await storageManager.get<boolean>(
      STORAGE_KEYS.ONESIGNAL_PERMISSION_PROMPTED,
    );

    if (alreadyPrompted) {
      const hasPermission = await OneSignal.Notifications.hasPermission();
      console.log('[OneSignal] Permission already requested previously:', {
        platform: 'native',
        hasPermission,
      });
      await logSubscriptionState('after-permission-check');
      return hasPermission;
    }

    const granted = await OneSignal.Notifications.requestPermission(false);

    await storageManager.set(STORAGE_KEYS.ONESIGNAL_PERMISSION_PROMPTED, true);

    if (granted) {
      console.log('[OneSignal] Permission granted');
    } else {
      console.log('[OneSignal] Permission denied');
    }

    await logSubscriptionState('after-permission-request');

    return granted;
  } catch (error) {
    console.error('[OneSignal] Permission request failed:', error);
    return false;
  }
}

export async function syncOneSignalUserNative(params: SyncOneSignalUserParams): Promise<void> {
  const parsed = parseSyncParams(params);
  if (!parsed) return;

  await runSerializedSync(async () => {
    await initializeOneSignalNative();
    if (!isOneSignalNativeAvailable()) return;

    const { artistId, email, phone, needsLogin, needsEmail, needsPhone } = parsed;

    try {
      if (needsLogin) {
        await OneSignal.login(artistId);
        setSyncedExternalId(artistId);
      }

      if (needsEmail && email) {
        await OneSignal.User.addEmail(email);
        setLastSyncedEmail(email);
      }

      if (needsPhone && phone) {
        await OneSignal.User.addSms(phone);
        setLastSyncedPhone(phone);
      }

      console.log('[OneSignal] User synced', {
        platform: 'native',
        artistId,
        hasEmail: Boolean(email),
        hasPhone: Boolean(phone),
      });

      await logSubscriptionState('after-login');

      const subscriptionId = await OneSignal.User.pushSubscription.getIdAsync();
      const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
      await registerPushSubscriptionWithBackend(subscriptionId, platform);
    } catch (error) {
      console.warn('[OneSignal] User sync failed:', error);
    }
  });
}

export async function logOutOneSignalNative(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !getOneSignalAppId()) {
    clearOneSignalSessionState();
    return;
  }

  try {
    let subscriptionId: string | null = null;
    if (initialized) {
      subscriptionId = (await OneSignal.User.pushSubscription.getIdAsync()) ?? null;
    }

    await deactivatePushSubscriptionOnBackend(subscriptionId);

    if (initialized) {
      await OneSignal.logout();
      console.log('[OneSignal] User logged out');
    }
  } catch (error) {
    console.warn('[OneSignal] logOut failed:', error);
  } finally {
    clearOneSignalSessionState();
  }
}
